import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SelfServiceDashboard from './selfServiceDashboard';
import { useUserPermissions } from '../context/UserPermissionsContext';
import { broadcastDCChange } from '../utils/dcEvents';
import { normalizeCapabilityKey } from '../services/settingsServices/userRolesService';

interface SelfServiceContextProps {
    activeTab?: number;
}

interface OrgData {
    id: string;
    name: string;
}

const SelfServiceContext: React.FC<SelfServiceContextProps> = ({ activeTab = 0 }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { groupName, subGroup } = location.state || {};
    const [tabIndex, setTabIndex] = useState(activeTab);
    
    const { userPermissions } = useUserPermissions();
    
   
    // Helper: normalize DC codes to 'W' + <CODE> (Dashboard stores like 'WABC')
    const normalizeDC = (dc: string) => {
    if (!dc) return dc;
    const up = dc.toUpperCase();
    return up.startsWith('W') ? up : `W${up}`;
    };

    // Organization states
    // ✅ Initialize from sessionStorage so Self Service starts with Dashboard's DC before effects run
    const [selectedOrg, setSelectedOrg] = useState<string>(() =>
        normalizeDC(sessionStorage.getItem('selectedDC') || '')
    );

    // Fix type name from `orgdata` to `OrgData`
    const [orgLoading] = useState<boolean>(false);
 
    
    // Only show DCs where user has self_service or banana_sequencing capability
    const allowedSelfServiceDCs = useMemo(() => {
        if (!userPermissions) return [];
        const dcSet = new Set<string>();
        (userPermissions.roles || []).forEach(role => {
            const caps = (role.capabilities || []).map(c => normalizeCapabilityKey(c));
            if (role.dcCode && caps.some(c => c.includes('selfservice') || c.includes('banana'))) {
                dcSet.add(role.dcCode);
            }
        });
        return Array.from(dcSet);
    }, [userPermissions]);

    // Check if user has access for the currently selected DC
    const hasAccessForSelectedOrg = useMemo(() => {
        if (!userPermissions || !selectedOrg) return false;
        return userPermissions.roles
            .filter(role => role.dcCode === selectedOrg)
            .some(role =>
                (role.capabilities || []).some(cap => {
                    const normalized = normalizeCapabilityKey(cap);
                    return normalized.includes('selfservice') || normalized.includes('banana');
                })
            );
    }, [userPermissions, selectedOrg]);

    // Create organization options from allowed self-service DCs
    const orgOptions: OrgData[] = useMemo(() => {
    const storedNormalized = normalizeDC(sessionStorage.getItem('selectedDC') || '');

    const baseOrgs = allowedSelfServiceDCs.map(dc => ({
        id: normalizeDC(dc),
        name: normalizeDC(dc),
    }));

    if (storedNormalized && !baseOrgs.some(org => org.id === storedNormalized)) {
        return [
            {
                id: storedNormalized,
                name: storedNormalized,
            },
            ...baseOrgs,
        ];
    }

    return baseOrgs;
}, [allowedSelfServiceDCs]);
    

    /**
     * Effect 2: If selectedOrg is still empty at this point, fallback to the first option.
     * (This will NOT override a valid stored selection.)
     */
    useEffect(() => {
    if (!selectedOrg && orgOptions.length > 0) {
        setSelectedOrg(orgOptions[0].id);
    }
    }, [orgOptions, selectedOrg]);

    /**
     * Effect 3: Keep storage updated when the user changes org in Self Service.
     */
    useEffect(() => {
    if (selectedOrg) {
        sessionStorage.setItem('selectedDC', selectedOrg);
        broadcastDCChange(selectedOrg);
    }
    }, [selectedOrg]);


    return (
        <div>
            <div className='h-[10px] w-full px-7' style={{ backgroundColor: 'white' }}>
                <div className='flex items-center mt-1'>
                    <span className='md:text-[28px] text-[24px] whitespace-nowrap font-bold '>
                        SELF SERVICE HUB
                    </span>
                    <div className='dropDownContainer'>
                        {/* Organization Dropdown */}
                        <select
                            value={selectedOrg}
                            onChange={(e) => setSelectedOrg(e.target.value)}
                            disabled={orgLoading}
                            className={`customSelect ${orgLoading ? 'disabled' : 'enabled'}`}
                        >
                            <option value="">Select Organization</option>
                            {orgOptions.map((org) => (
                                <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
               <div className="tabs">
                {(() => {                    
                    const tabConfigs = [
                        {
                            id: 0,
                            operationType: 'selfService',
                            label: subGroup || 'Self Service Dashboard',
                            route: '/wms-upload-ui/self-service',
                            requiresAuth: true
                        }                      
                    ];                    
                    return tabConfigs.map(tab => {                        
                        return (
                            <div
                                key={tab.id}
                                className={`tab ${tabIndex === tab.id ? 'active' : ''}`}
                                onClick={() => {
                                    setTabIndex(tab.id);
                                    navigate(tab.route);
                                }}
                            >
                                {tab.label}
                            </div>
                        );
                    }).filter(Boolean);
                })()}
            </div> 
              <div className="tab-content">
                {!selectedOrg ? (
                    <div className='under-construction'>Please select an organization to continue.</div>
                ) : !hasAccessForSelectedOrg ? (
                    <div className='under-construction'>You don't have access to this operation for the selected DC.</div>
                ) : (
                    <SelfServiceDashboard
                        selectedOrg={selectedOrg}
                        selectedGroup={groupName}
                        selectedSubGroup={subGroup}
                        tabType="selfServiceDashboard"
                        onUpload={(files: File[]) => {}}
                        onUploadFilTable={(filesFillTable: File[]) => {}}
                    />
                )}
            </div>
        </div>
    );
};

export default SelfServiceContext;
